import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import * as XLSX from 'xlsx-js-style';
import './ShoppingCart.css';
import CartQuoteRequestForm from './CartQuoteRequestForm';
import { formatAandeelDisplay } from '../utils/aandeelDisplay';

const ShoppingCart = () => {
  const { 
    cartItems, 
    removeFromCart, 
    clearCart, 
    applyBulkDiscount,
    applyDiscount 
  } = useCart();
  const { t } = useLanguage();
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountValue, setDiscountValue] = useState('');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showItemDiscountModal, setShowItemDiscountModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Close popover when clicking outside and update position on scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.info-popover-container')) {
        setExpandedItems(new Set());
      }
    };

    const handleScroll = () => {
      // Update position of all open popovers
      expandedItems.forEach(itemId => {
        const container = document.querySelector(`[data-item-id="${itemId}"]`);
        if (container) {
          const button = container.querySelector('.info-popover-btn');
          const popover = container.querySelector('.info-popover');
          if (button && popover) {
            Object.assign(popover.style, positionPopover(button));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [expandedItems]);

  // Parse price from string format to float
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    console.log('Parsing price string:', priceStr);
    
    // Remove € symbol and spaces
    let cleanedPrice = priceStr.replace(/€/g, '').trim();
    
    // Replace dots (thousands separators) with nothing
    cleanedPrice = cleanedPrice.replace(/\./g, '');
    
    // Replace comma (decimal separator) with dot
    cleanedPrice = cleanedPrice.replace(/,/g, '.');
    
    console.log('Cleaned price string:', cleanedPrice);
    
    // Parse to float
    const result = parseFloat(cleanedPrice) || 0;
    console.log('Parsed price result:', result);
    return result;
  };

  // Format price to European format with comma as decimal separator
  const formatPrice = (price) => {
    console.log('Formatting price input:', price, 'Type:', typeof price);
    
    // Ensure price is a number
    let numPrice;
    if (typeof price === 'string') {
      // If it's already a formatted string with a comma, parse it first
      if (price.includes(',')) {
        numPrice = parsePrice(price);
      } else {
        // Try to parse it as a regular number
        numPrice = parseFloat(price) || 0;
      }
    } else {
      numPrice = parseFloat(price) || 0;
    }
    
    console.log('Numeric price for formatting:', numPrice);
    
    // Format to 2 decimal places with comma as separator and dot for thousands
    const formattedPrice = numPrice.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    console.log('Formatted price result:', formattedPrice);
    
    return formattedPrice;
  };

  // Calculate total price of all items in cart
  const calculateTotal = useCallback(() => {
    console.log('Calculating total for cart items:', cartItems);
    
    let total = 0;
    
    cartItems.forEach(item => {
      // Use calculatedPrice if available, otherwise parse from price string
      let itemPrice;
      if (item.calculatedPrice) {
        itemPrice = parseFloat(item.calculatedPrice);
        console.log(`Item ${item.title} using calculatedPrice:`, itemPrice);
      } else {
        itemPrice = parsePrice(item.price);
        console.log(`Item ${item.title} using parsed price:`, itemPrice);
      }
      
      // Apply discount if present
      if (item.discountPercentage) {
        itemPrice = itemPrice * (1 - item.discountPercentage / 100);
        console.log(`Item ${item.title} after discount:`, itemPrice);
      }
      
      // Add to total
      total += itemPrice;
    });
    
    console.log('Final total:', total);
    
    // Format total price with comma as decimal separator and dot for thousands
    const formattedTotal = total.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    setTotalPrice(formattedTotal);
    
    return formattedTotal;
  }, [cartItems]);

  // Calculate total price whenever cart items change
  useEffect(() => {
    calculateTotal();
  }, [cartItems, calculateTotal]);

  // Format number with dot as thousands separator, no decimals
  const formatWholeNumber = (number) => {
    if (number === null || isNaN(number)) return '0';
    
    // Handle decimal values properly
    let numValue;
    if (typeof number === 'string') {
      // If it's a string, remove dots and replace comma with dot for proper parsing
      numValue = parseFloat(number.replace(/\./g, '').replace(',', '.'));
    } else {
      numValue = parseFloat(number);
    }
    
    if (isNaN(numValue)) return '0';
    
    // Round to whole number
    const intValue = Math.round(numValue);
    let formattedNumber = intValue.toString();
    
    // Add thousands separator (dot)
    if (intValue >= 1000) {
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    
    return formattedNumber;
  };

  // Calculate impressions from bereik value
  const calculateImpressions = (bereik) => {
    if (!bereik) return 0;
    
    // Handle string values with comma as decimal separator and dot as thousands separator
    let bereikValue;
    if (typeof bereik === 'string') {
      // Remove dots (thousand separators) and replace comma with dot for parsing
      bereikValue = parseFloat(bereik.replace(/\./g, '').replace(',', '.'));
    } else {
      bereikValue = parseFloat(bereik);
    }
    
    if (isNaN(bereikValue)) return 0;
    
    // Multiply by 1000 to get impressions
    return bereikValue * 1000;
  };

  // Calculate bruto total for an item
  const calculateBrutoTotal = (item) => {
    // Parse base price
    let basePrice;
    if (typeof item.basePrice === 'string') {
      basePrice = parsePrice(item.basePrice);
    } else {
      basePrice = parseFloat(item.basePrice) || 0;
    }
    
    // Parse factors - handle Dutch number format (comma as decimal separator)
    const positionFactor = parseFloat((item.positionFactor || '1').toString().replace(',', '.')) || 1;
    const formatFactor = parseFloat((item.formatFactor || '1').toString().replace(',', '.')) || 1;
    const seasonalFactor = 1.0; // Always 1.0
    
    // Calculate bereik multiplier
    let bereikMultiplier = 1;
    if (item.medium !== 'Print' && item.bereik) {
      bereikMultiplier = parseFloat(item.bereik.replace(/\./g, '').replace(',', '.')) || 1;
    }
    
    // Calculate bruto total: bereik * basePrice * positionFactor * formatFactor * seasonalFactor
    const brutoTotal = bereikMultiplier * basePrice * positionFactor * formatFactor * seasonalFactor;
    
    return brutoTotal;
  };

  // Calculate netto total for an item
  const calculateNettoTotal = (item) => {
    const brutoTotal = calculateBrutoTotal(item);
    const discountPercentage = parseFloat(item.discountPercentage || 0);
    
    // Calculate netto total: bruto * (1 - discount/100)
    const nettoTotal = brutoTotal * (1 - discountPercentage / 100);
    
    return nettoTotal;
  };

  // Bereken de korting op basis van de prijs en het kortingspercentage
  const getDiscountPrice = (basePrice, discountPercentage) => {
    if (!basePrice || !discountPercentage) return 0;
    
    // Zorg dat basePrice een nummer is
    let numericBasePrice;
    if (typeof basePrice === 'string') {
      numericBasePrice = parsePrice(basePrice);
    } else {
      numericBasePrice = parseFloat(basePrice) || 0;
    }
    
    // Bereken de korting als een percentage van de basisprijs
    const discountAmount = numericBasePrice * (discountPercentage / 100);
    return discountAmount;
  };

  // Export cart items to XLS
  const exportToPlan = () => {
    // If cart is empty, don't do anything
    if (cartItems.length === 0) return;
    
    // Create data for Excel
    const excelData = [];
    
    // Add headers as first row based on the image
    excelData.push([
      'Titel',
      'Medium',
      'Medium Type',
      'Positie',
      'Formaat',
      'Bereik x 1.000 contacten',
      'Aantal (stuks of x1.000)',
      'Basisprijs (excl. -factor)',
      'Pos.factor',
      'Form.factor',
      'Targeting (CPM, vCPM)',
      'Plaatsings- of einddatum 2025',
      'Seizoens- factor',
      'Bruto Totaal',
      'Korting',
      'Netto Totaal exclusief BTW'
    ]);
    
    // Add each cart item as a row
    cartItems.forEach(item => {
      const impressions = item.impressions || (item.bereik ? calculateImpressions(item.bereik) : '');
      const originalPrice = item.originalPrice || item.price;
      
      // Clean up original price if it contains '€' symbol
      const cleanOriginalPrice = typeof originalPrice === 'string' && originalPrice.includes('€')
        ? originalPrice.replace('€', '').trim()
        : originalPrice;
      
      // Calculate final price with discount if applicable
      let finalPrice = originalPrice;
      
      if (item.discountPercentage) {
        // Use calculatedPrice if available, otherwise parse from price string
        let priceValue;
        if (item.calculatedPrice) {
          priceValue = parseFloat(item.calculatedPrice);
        } else {
          priceValue = parsePrice(originalPrice);
        }
        
        const discountedPrice = priceValue * (1 - item.discountPercentage / 100);
        finalPrice = priceValue;
      }
      
      // Format price values without € symbol
      const formattedOriginalPrice = typeof cleanOriginalPrice === 'string' ? cleanOriginalPrice : formatPrice(cleanOriginalPrice);
      const formattedFinalPrice = typeof finalPrice === 'string' ? finalPrice.replace('€', '').trim() : formatPrice(finalPrice);
      
      // Format bereik
      const bereikValue = item.bereik ? item.bereik : (impressions ? (impressions / 1000) : '');
      
      // Format discount
      const discountValue = item.discountPercentage ? item.discountPercentage : 0;
      // Format discount price (prijs - (prijs * discount))
      const discountPriceValue = getDiscountPrice(item.basePrice, discountValue);
      
      // Add row with the format from the image
      excelData.push([
        item.title || '',                           // Titel
        item.medium || '',                          // Medium
        item.originalData?.Mediumtype || '',        // Medium Type
        item.position || '',                        // Positie (geen fallback)
        item.format || '',                          // Formaat (geen fallback)
        // Gebruik de originele bereik waarde zonder formattering
        bereikValue,                                // Bereik x 1.000 contacten (zonder 'Oplage: ' prefix)
        '1',                                        // Aantal (stuks of x1.000)
        // Gebruik de originele Basisprijs waarde zonder formattering
        item.basePrice || '',                       // Basisprijs (originele waarde)
        item.positionFactor || '',                  // Pos.factor
        item.formatFactor || '',                    // Form.factor
        item.targetingFactor || '',                 // Targeting (geen fallback)
        item.plaatsingsdatum || '',                 // Plaatsingsdatum (geen fallback)
        '1,0',                                      // Seizoensfactor (altijd 1,0)
        // Bereken bruto totaal als vaste waarde
        calculateBrutoTotal(item),                  // Bruto Totaal (berekende waarde)
        // Store discount as a number without % sign for Excel calculations
        discountValue, // Korting (default 0)
        // Bereken netto totaal als vaste waarde
        calculateNettoTotal(item)                   // Netto Totaal (berekende waarde)
      ]);
    });
    
    // Bepaal de laatste rij (voor de totalen)
    const lastDataRow = excelData.length;
    
    // Calculate totals as static values
    let totalBruto = 0;
    let totalNetto = 0;
    
    // Calculate totals from all items
    cartItems.forEach(item => {
      totalBruto += calculateBrutoTotal(item);
      totalNetto += calculateNettoTotal(item);
    });
    
    // Add totals row with calculated values instead of Excel formulas
    excelData.push([
      '',                                        // Titel
      '',                                        // Medium
      '',                                        // Medium Type
      '',                                        // Positie
      '',                                        // Formaat
      '',                                        // Bereik
      '',                                        // Aantal
      '',                                        // Basisprijs
      '',                                        // Pos.factor
      '',                                        // Form.factor
      '',                                        // Targeting
      '',                                        // Plaatsingsdatum
      '',                                        // Seizoensfactor
      totalBruto,                                // Bruto Totaal (berekende waarde)
      '',                                        // Korting (leeg, geen gemiddelde berekening meer)
      totalNetto                                 // Netto Totaal (berekende waarde)
    ]);
    
    // Create a worksheet from the data
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Define styles based on the image
    const headerStyle = {
      font: { 
        bold: true, 
        color: { rgb: "FFFFFF" } 
      },
      fill: { 
        patternType: "solid", 
        fgColor: { rgb: "000000" } // Black background like in the image
      },
      alignment: { 
        horizontal: "center", 
        vertical: "center",
        wrapText: true
      },
      border: {
        top: { style: "medium", color: { rgb: "000000" } },
        bottom: { style: "medium", color: { rgb: "000000" } },
        left: { style: "medium", color: { rgb: "000000" } },
        right: { style: "medium", color: { rgb: "000000" } }
      }
    };

    // Standard cell style for all data cells
    const standardCellStyle = {
      alignment: {
        vertical: "center",
        wrapText: true
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
    
    // Light gray background style for specific columns
    const lightGrayStyle = {
      ...standardCellStyle,
      fill: {
        patternType: "solid",
        fgColor: { rgb: "F0F0F0" } // Light gray background
      }
    };
    
    // Total row style (black like header)
    const totalRowStyle = {
      font: { 
        bold: true, 
        color: { rgb: "FFFFFF" } 
      },
      fill: { 
        patternType: "solid", 
        fgColor: { rgb: "000000" } 
      },
      alignment: { 
        horizontal: "center", 
        vertical: "center",
        wrapText: true
      },
      border: {
        top: { style: "medium", color: { rgb: "000000" } },
        bottom: { style: "medium", color: { rgb: "000000" } },
        left: { style: "medium", color: { rgb: "000000" } },
        right: { style: "medium", color: { rgb: "000000" } }
      }
    };
    
    // Apply styles to all cells
    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Apply header style to first row
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
      ws[cellRef].s = headerStyle;
    }
    
    // Apply style to the totals row (last row)
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: range.e.r, c: C });
      
      // Apply total row style with appropriate horizontal alignment
      ws[cellRef].s = {
        ...totalRowStyle,
        alignment: {
          ...totalRowStyle.alignment,
          horizontal: C >= 10 ? "right" : "center" // Right align the total numbers
        }
      };
    }
    
    // Set uniform row heights for better vertical spacing
    ws['!rows'] = [];
    for (let i = 0; i <= range.e.r; i++) {
      ws['!rows'][i] = { hpx: 35 }; // All rows have 35 pixels height for better spacing
    }
    
    // Apply styles to all regular data cells
    for (let R = 1; R < range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;
        
        // Basic style for all cells with appropriate alignment
        let baseStyle = standardCellStyle;
        
        // Apply light gray background to specific columns:
        // In de excelData array hebben we deze volgorde:
        // 0:Titel, 1:Medium, 2:Medium Type, 3:Positie, 4:Formaat, 5:Bereik, 6:Aantal, 7:Basisprijs, 8:Pos.factor, 9:Form.factor, 10:Targeting, 
        // 11:Plaatsingsdatum, 12:Seizoensfactor, 13:Bruto, 14:Korting, 15:Netto
        if (C === 5 || C === 7 || C === 8 || C === 10 || C === 12) {
          baseStyle = lightGrayStyle;
        }
        
        ws[cellRef].s = { 
          ...baseStyle,
          alignment: {
            ...baseStyle.alignment,
            horizontal: (C === 10 || C === 12 || C === 13) ? "right" : "left"
          }
        };
        
        // Apply number formats to price columns
        if (C === 7 || C === 13 || C === 15) {
          ws[cellRef].s = {
            ...ws[cellRef].s,
            numFmt: '€ #,##0.00' // Currency format for Euro
          };
        }
        
        // Apply percentage format to discount column
        if (C === 14) {
          ws[cellRef].s = {
            ...ws[cellRef].s,
            numFmt: '0.00"%"' // Percentage format
          };
        }
      }
    }
    
    // Apply column widths
    ws['!cols'] = [
      { wch: 20 },  // Titel
      { wch: 15 },  // Medium
      { wch: 15 },  // Medium Type
      { wch: 25 },  // Positie (wider to fit the position text)
      { wch: 15 },  // Formaat
      { wch: 15 },  // Bereik
      { wch: 10 },  // Aantal
      { wch: 15 },  // Basisprijs
      { wch: 10 },  // Pos.factor
      { wch: 10 },  // Form.factor
      { wch: 15 },  // Targeting
      { wch: 20 },  // Plaatsingsdatum
      { wch: 10 },  // Seizoensfactor
      { wch: 15 },  // Bruto Totaal
      { wch: 10 },  // Korting
      { wch: 20 }   // Netto Totaal exclusief BTW (wider for the full text)
    ];
    
    // Apply number formats to the total row
    const totalRow = range.e.r;
    
    // Format for Bruto Totaal in total row
    const brutoCellRef = XLSX.utils.encode_cell({ r: totalRow, c: 13 });
    if (ws[brutoCellRef]) {
      ws[brutoCellRef].s = {
        ...ws[brutoCellRef].s,
        numFmt: '€ #,##0.00' // Currency format
      };
    }
    
    // Format for Netto Totaal in total row
    const nettoCellRef = XLSX.utils.encode_cell({ r: totalRow, c: 15 });
    if (ws[nettoCellRef]) {
      ws[nettoCellRef].s = {
        ...ws[nettoCellRef].s,
        numFmt: '€ #,##0.00' // Currency format
      };
    }
    
    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mediaplan');
    
    // Generate filename with date
    const fileName = `mediaplan-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, fileName);
  };

  const testSimpleExport = () => {
    // Create a simple dataset with basic styling
    const data = [
      ['Header 1', 'Header 2', 'Header 3'], // Header row
      ['Data A1', 'Data B1', 'Data C1'],    // Data row 1
      ['Data A2', 'Data B2', 'Data C2'],    // Data row 2
    ];
    
    // Create a worksheet from the data
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Apply styles to the header row (first row)
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { patternType: "solid", fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center" }
    };
    
    // Apply styles to the data cells
    const dataStyle = {
      font: { color: { rgb: "000000" } },
      fill: { patternType: "solid", fgColor: { rgb: "E2EFDA" } }, // Light green
      alignment: { horizontal: "left" }
    };
    
    // Apply header styles to A1:C1
    ws['A1'].s = headerStyle;
    ws['B1'].s = headerStyle;
    ws['C1'].s = headerStyle;
    
    // Apply data styles to row 2
    ws['A2'].s = dataStyle;
    ws['B2'].s = dataStyle;
    ws['C2'].s = dataStyle;
    
    // Set column widths
    ws['!cols'] = [{ width: 20 }, { width: 20 }, { width: 20 }];
    
    // Create a new workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Test");
    
    // Write the file
    XLSX.writeFile(wb, "test-styling.xlsx");
  };

  // Handle discount input change
  const handleDiscountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers between 0-99
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 99)) {
      setDiscountValue(value);
    }
  };
  
  // Handle key press in discount input
  const handleDiscountKeyPress = (e) => {
    if (e.key === 'Enter' && discountValue !== '') {
      if (selectedItemId) {
        handleApplyItemDiscount();
      } else {
        handleApplyDiscount();
      }
    }
  };
  
  const handleApplyDiscount = () => {
    if (discountValue === '') return;
    
    const discountPercentage = parseInt(discountValue);
    
    // Apply discount to all items at once
    applyBulkDiscount(discountPercentage);
    
    // Close the modal
    setShowDiscountModal(false);
  };

  const handleApplyItemDiscount = () => {
    if (discountValue === '' || !selectedItemId) return;
    
    const discountPercentage = parseInt(discountValue);
    
    // Apply discount to the selected item
    applyDiscount(selectedItemId, discountPercentage);
    
    // Close the modal
    setShowItemDiscountModal(false);
    setSelectedItemId(null);
  };

  const handleApplyItemDiscountToAll = () => {
    if (discountValue === '') return;
    
    const discountPercentage = parseInt(discountValue);
    
    // Apply discount to all items
    applyBulkDiscount(discountPercentage);
    
    // Close the modal
    setShowItemDiscountModal(false);
    setSelectedItemId(null);
  };
  
  // Close discount modal
  const handleCloseDiscountModal = () => {
    setDiscountValue('');
    setShowDiscountModal(false);
  };

  // Close item discount modal
  const handleCloseItemDiscountModal = () => {
    setDiscountValue('');
    setShowItemDiscountModal(false);
    setSelectedItemId(null);
  };
  
  // Get the display price with discount if applicable (always 2 decimals, comma as separator)
  const getDisplayPrice = (item) => {
    console.log('Getting display price for item:', item);

    // Resolve numeric price for formatting
    let priceValue;
    if (item.calculatedPrice != null) {
      priceValue = parseFloat(item.calculatedPrice);
    } else {
      const raw = item.originalPrice || item.price;
      priceValue = parsePrice(raw);
    }

    const formattedPrice = formatPrice(priceValue);
    const priceWithEuro = formattedPrice.includes('€') ? formattedPrice.trim() : `€ ${formattedPrice}`;

    if (!item.discountPercentage) {
      return <span className="price-value">{priceWithEuro}</span>;
    }

    const discountPercentage = item.discountPercentage;
    const discountedPrice = priceValue * (1 - discountPercentage / 100);
    const formattedDiscountedPrice = formatPrice(discountedPrice);
    const cleanDiscountedPrice = formattedDiscountedPrice.includes('€')
      ? formattedDiscountedPrice.trim()
      : `€ ${formattedDiscountedPrice}`;

    return (
      <div className="discounted-price">
        <span className="original-price">{priceWithEuro}</span>
        <span className="final-price">{cleanDiscountedPrice}</span>
      </div>
    );
  };

  // Get the title for the discount modal
  const getDiscountModalTitle = () => {
    return selectedItemId 
      ? t('applyDiscountToItem') 
      : t('applyDiscountToAllItems');
  };

  // Get the selected item for individual discount
  const getSelectedItem = () => {
    if (!selectedItemId) return null;
    return cartItems.find(item => item.id === selectedItemId);
  };

  // Check if all items have the same discount
  const getCommonDiscount = () => {
    if (cartItems.length === 0) return null;
    
    const firstDiscount = cartItems[0].discountPercentage;
    if (firstDiscount === undefined) return null;
    
    const allSameDiscount = cartItems.every(item => 
      item.discountPercentage === firstDiscount
    );
    
    return allSameDiscount ? firstDiscount : null;
  };

  // Check if there are mixed discounts in the cart
  const hasMixedDiscounts = () => {
    if (cartItems.length <= 1) return false;
    
    // Check if at least one item has a discount
    const hasAnyDiscount = cartItems.some(item => item.discountPercentage !== undefined);
    
    // If there's a common discount, then it's not mixed
    if (getCommonDiscount() !== null) return false;
    
    // If there's at least one discount but no common discount, then it's mixed
    return hasAnyDiscount;
  };

  // Display the cart total with the correct formatting (always 2 decimals, comma as separator)
  const displayTotal = () => {
    if (typeof totalPrice === 'string' && totalPrice.includes('€')) {
      return totalPrice.trim();
    }
    if (typeof totalPrice === 'number') {
      return `€ ${formatPrice(totalPrice)}`;
    }
    return `€ ${totalPrice}`;
  };

  // Parse Dutch currency format (comma as decimal separator, dot as thousands separator)
  const parseDutchCurrency = (currencyStr) => {
    if (!currencyStr) return 0;
    
    return parseFloat(
      currencyStr.toString()
        .replace(/€/g, '')        // Remove euro symbol
        .replace(/\./g, '')       // Remove thousand separators (dots in Dutch format)
        .replace(/,/g, '.')       // Convert comma decimal separator to period for JavaScript
        .trim()
    );
  };

  // Parse numeric factor from CSV (0.18) or Dutch (0,18) into a number
  const parseFormatFactor = (factor) => {
    if (factor === null || factor === undefined) return 1;
    const str = factor.toString().trim();
    if (!str) return 1;
    // If comma present: Dutch format (comma = decimal). Otherwise dot = decimal (e.g. from CSV).
    const num = str.includes(',')
      ? parseDutchCurrency(factor)
      : parseFloat(str);
    return (Number.isNaN(num) ? 1 : num) || 1;
  };

  // Format a factor for display: comma as decimal separator (0,18). Reused for position and format factor.
  const formatFactorForDisplay = (factor) => {
    const numFactor = parseFormatFactor(factor);
    if (numFactor === 1) return '1';
    const formatted = numFactor.toFixed(2).replace('.', ',').replace(/,\d*0+$/, (m) => m.replace(/0+$/, ''));
    // Whole numbers like 2,00 become "2," after trimming zeros — drop the stray comma
    return formatted.replace(/,$/, '');
  };

  const formatFormatFactor = formatFactorForDisplay;
  const formatPositionFactor = formatFactorForDisplay;

  // Toggle item info expansion - only allow one popover open at a time
  const toggleItemInfo = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        // If clicking the same item, close it
        newSet.delete(itemId);
      } else {
        // If clicking a different item, close all others and open this one
        newSet.clear();
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Position popover relative to button
  const positionPopover = (buttonElement) => {
    if (!buttonElement) return {};
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const popoverHeight = 200; // Estimated height
    
    // Calculate position
    let top = rect.bottom + 8;
    let left = rect.left + (rect.width / 2);
    
    // Check if popover would go below viewport
    if (top + popoverHeight > viewportHeight) {
      // Position above the button
      top = rect.top - popoverHeight - 8;
    }
    
    // Ensure popover doesn't go off-screen horizontally
    const popoverWidth = 300; // min-width
    if (left + popoverWidth / 2 > window.innerWidth) {
      left = window.innerWidth - popoverWidth / 2 - 10;
    } else if (left - popoverWidth / 2 < 0) {
      left = popoverWidth / 2 + 10;
    }
    
    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'translateX(-50%)'
    };
  };

  // Position popover when it opens
  useEffect(() => {
    expandedItems.forEach(itemId => {
      const button = document.querySelector(`[data-button-id="${itemId}"]`);
      const popover = document.querySelector(`[data-item-id="${itemId}"] .info-popover`);
      if (button && popover) {
        Object.assign(popover.style, positionPopover(button));
      }
    });
  }, [expandedItems]);

  // Get calculation details for display
  const getCalculationDetails = (item) => {
    console.log('Getting calculation details for item:', item);
    const basePrice = item.basePrice || 0;
    const price = item.price || 0;
    const positionFactor = item.positionFactor || 1;
    const formatFactor = item.formatFactor || 1;
    const bereik = item.bereik || 1;
    
    // Format the calculation string
    let calculation = '';
    let labels = '';
    const tariefbasis = (item.targetingFactor || '').toLowerCase();
    const isFixedPrice = tariefbasis.includes('fixed');

    if (isFixedPrice) {
      labels = t('calculationLabelFixedPrice', 'Prijs per plaatsing');
    } else if (item.medium === 'Print') {
      // For print, calculation is: basePrice × positionFactor × formatFactor (Dutch decimal notation)
      calculation = `${formatPrice(basePrice)} × ${formatPositionFactor(positionFactor)} × ${formatFormatFactor(formatFactor)}`;
      labels = t('calculationLabelsPrint');
    } else if (item.medium === 'Nieuwsbrief') {
      // For nieuwsbrief, calculation is similar: basePrice * positionFactor * formatFactor
      calculation = `${formatPrice(price)} `;
      labels = t('calculationLabelsNieuwsbrief');
    } else {
      // For digital, calculation is: bereik × basePrice × positionFactor × formatFactor (Dutch decimal notation)
      calculation = `${bereik} × ${formatPrice(basePrice)} × ${formatPositionFactor(positionFactor)} × ${formatFormatFactor(formatFactor)}`;
      labels = t('calculationLabelsDigital');
    }

    if (isFixedPrice && !calculation) {
      calculation = formatPrice(price);
    }

    return { calculation, labels };
  };

  return (
    <div className="shopping-cart">
      <h2 className="cart-title">{t('shoppingCart')}</h2>
      
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <p>{t('emptyCart')}</p>
          <p className="empty-cart-subtext">{t('addItemsToGetStarted')}</p>
        </div>
      ) : (
        <>
          <div className="cart-header-actions">
            {getCommonDiscount() !== null && (
              <div className="common-discount-notice">
                <span className="discount-info">
                  {t('allItemsHaveDiscount', { discount: getCommonDiscount() })}
                </span>
                <span className="discount-info-subtext">
                  {t('newItemsReceiveDiscount')}
                </span>
              </div>
            )}
            {hasMixedDiscounts() && (
              <div className="mixed-discount-notice">
                <span className="discount-info">
                  {t('mixedDiscountsApplied')}
                </span>
                <span className="discount-info-subtext">
                  {t('checkIndividualDiscounts')}
                </span>
              </div>
            )}
            <button 
              className="discount-cart-btn" 
              onClick={() => {
                setDiscountValue('');
                setShowDiscountModal(true);
              }}
            >
              <svg 
                className="discount-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <line x1="19" y1="5" x2="5" y2="19"></line>
                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                <circle cx="17.5" cy="17.5" r="2.5"></circle>
              </svg>
              <span>
                {getCommonDiscount() !== null 
                  ? t('editDiscount', { discount: getCommonDiscount() }) 
                  : t('applyDiscount')
                }
              </span>
            </button>
          </div>
          <div className="cart-items">
            <div className="cart-header">
              <div className="cart-header-item product-info">{t('productDetails')}</div>
              <div className="cart-header-item">{t('mediumType')}</div>
              <div className="cart-header-item">{t('calculation')}</div>
              <div className="cart-header-item">{t('price')}</div>
              <div className="cart-header-item"></div>
            </div>
            
            {cartItems.map((item, index) => {
              const isExpanded = expandedItems.has(item.id);
              
              return (
                <div key={item.id || index} className="cart-item" data-item-id={item.id}>
                  <div className="product-info">
                    <div className="product-info-content">
                      <div className="product-title">{item.title}</div>
                      <div className="product-format">{item.format}</div>
                      {(item.bereik || item.medium === 'Print') && (
                        <div className="product-bereik">
                          <span className="bereik-label">
                            {item.medium === 'Online' ? t('impressions') : t('bereik')}:
                          </span>  
                          <span className="impressions-info">
                            {formatWholeNumber(item.impressions || calculateImpressions(item.bereik) || 0)} 
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="info-popover-container">
                      <button 
                        className={`info-popover-btn ${isExpanded ? 'active' : ''}`}
                        onClick={() => toggleItemInfo(item.id)}
                        title={isExpanded ? t('hideDetails') : t('showDetails')}
                        data-button-id={item.id}
                      >
                        i
                      </button>
                      
                      {/* Floating popover */}
                      {isExpanded && (
                        <div className="info-popover">
                          <button 
                            className="popover-close-btn"
                            onClick={() => toggleItemInfo(item.id)}
                            title={t('close')}
                            aria-label={t('close')}
                          >
                            <svg 
                              className="close-icon" 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                          <div className="popover-content">
                            {item.originalData && (
                              <div className="popover-section">
                                <h4>{t('additionalInfo')}</h4>
                                {Object.entries(item.originalData).map(([key, value]) => {
                                  // Skip empty values and specific keys
                                  if (!value || value === '-' || value === '' || key === 'Offertetool' || key === 'Tarief-calculator') return null;
                                  
                                  return (
                                    <div key={key} className="popover-row">
                                      <span className="popover-label">{key}:</span>
                                      <span className="popover-value">
                                        {key === 'Aandeel' ? formatAandeelDisplay(value) : value}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="popover-arrow"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="product-medium">{item.medium}</div>
                  <div className="product-calculation">
                    <div className="calculation-formula">
                      {getCalculationDetails(item).calculation}
                    </div>
                    <div className="calculation-labels">
                      {getCalculationDetails(item).labels}
                    </div>
                  </div>
                  <div className="product-price">
                    {getDisplayPrice(item)}
                    <button 
                      className={`item-discount-btn ${item.discountPercentage ? 'has-discount' : ''}`}
                      onClick={() => {
                        setSelectedItemId(item.id);
                        setDiscountValue(item.discountPercentage?.toString() || '');
                        setShowItemDiscountModal(true);
                      }}
                      title={item.discountPercentage 
                        ? t('editDiscount', { discount: item.discountPercentage }) 
                        : t('applyDiscountToItem')}
                    >
                      <svg 
                        className="discount-icon" 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="12" 
                        height="12" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                      </svg>
                      {item.discountPercentage ? (
                        <span className="item-discount-value">{item.discountPercentage}</span>
                      ) : (
                        <span className="add-discount-text">{t('addDiscount')}</span>
                      )}
                    </button>
                  </div>
                  <div className="product-remove">
                    <button 
                      className="remove-btn"
                      onClick={() => removeFromCart(item.id)}
                      aria-label="Remove item"
                    >
                      <svg 
                        className="trash-icon" 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"></path>
                        <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                      </svg>
                    </button>
                  </div>
                  

                </div>
              );
            })}
          </div>
          
          <div className="cart-summary">
            <div className="cart-total">
              <span className="total-label">{t('total')}:</span>
              <span className="price-value">{displayTotal()}</span>
            </div>
            <button className="clear-cart-btn" onClick={clearCart}>
              <svg 
                className="trash-icon clear-cart-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"></path>
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
              </svg>
              <span>{t('clearCart')}</span>
            </button>
            <div className="cart-actions">
              <button 
                className="checkout-btn" 
                onClick={exportToPlan}
              >
                Plan exporteren
              </button>
              <button 
                className="checkout-btn" 
                onClick={() => setShowQuoteForm(true)}
                style={{ marginLeft: '10px' }}
              >
                Offerte aanvragen
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Bulk Discount Modal */}
      {showDiscountModal && (
        <div className="discount-modal-overlay">
          <div className="discount-modal">
            <h3>{t('applyDiscountToAllItems')}</h3>
            <p className="discount-explanation">{t('discountExplanation')}</p>
            <div className="discount-input-container">
              <label htmlFor="discount-input" className="discount-label">{t('discountPercentage')}</label>
              <div className="input-wrapper">
                <input
                  id="discount-input"
                  type="text"
                  value={discountValue}
                  onChange={handleDiscountChange}
                  onKeyPress={handleDiscountKeyPress}
                  placeholder="0-99"
                  className="discount-input"
                  autoFocus
                />
                <span className="discount-percent">%</span>
              </div>
            </div>
            
            <div className="discount-actions">
              <button 
                className="discount-cancel-btn"
                onClick={handleCloseDiscountModal}
              >
                {t('cancel')}
              </button>
              <button 
                className="discount-apply-btn"
                onClick={handleApplyDiscount}
                disabled={discountValue === ''}
              >
                {t('apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Item Discount Modal */}
      {showItemDiscountModal && (
        <div className="discount-modal-overlay">
          <div className="discount-modal">
            <h3>{t('applyDiscountToItem')}</h3>
            {getSelectedItem() && (
              <div className="selected-item-info">
                <p className="selected-item-title">{getSelectedItem().title}</p>
                <p className="selected-item-format">{getSelectedItem().format}</p>
              </div>
            )}
            <p className="discount-explanation">{t('itemDiscountExplanation')}</p>
            <div className="discount-input-container">
              <label htmlFor="item-discount-input" className="discount-label">{t('discountPercentage')}</label>
              <div className="input-wrapper">
                <input
                  id="item-discount-input"
                  type="text"
                  value={discountValue}
                  onChange={handleDiscountChange}
                  onKeyPress={handleDiscountKeyPress}
                  placeholder="0-99"
                  className="discount-input"
                  autoFocus
                />
                <span className="discount-percent">%</span>
              </div>
            </div>
            
            <div className="discount-actions">
              <button 
                className="discount-cancel-btn"
                onClick={handleCloseItemDiscountModal}
              >
                {t('cancel')}
              </button>
              <button 
                className="discount-apply-btn"
                onClick={handleApplyItemDiscount}
                disabled={discountValue === ''}
              >
                {t('apply')}
              </button>
            </div>
            
            <div className="apply-to-all-container">
              <button 
                className="apply-to-all-btn"
                onClick={handleApplyItemDiscountToAll}
                disabled={discountValue === ''}
              >
                {t('applyToAllItems')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Form Modal */}
      <CartQuoteRequestForm 
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        cartItems={cartItems}
        totalPrice={totalPrice}
      />
    </div>
  );
};

export default ShoppingCart; 